import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import WebIcon from './WebIcon';
import styles from './ItineraryEditor.styles';
import { mergePayload, buildMetaPairs, buildHighlightLines, resolveCardTypeMeta, getTypeIcon, arrayify } from './itineraryUtils';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const FallbackAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick }) => {
  const mergedPayload = mergePayload(it) || {};
  const title = it?.title || it?.card?.title || mergedPayload.title || '未命名';
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的）
  const description = it?.note || it?.description || mergedPayload.description || it?.card?.description || '';

  // Helper: safely extract string from value that may be object
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      const candidates = ['formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'city', 'province', 'country'];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
      // Try to join meaningful values
      const values = Object.values(value).filter(v => typeof v === 'string' && v.trim()).map(String);
      return values.length ? values.join(', ') : undefined;
    }
    return String(value).trim() || undefined;
  };

  const metaPairs = buildMetaPairs(it, mergedPayload);
  const highlightLines = buildHighlightLines(it, mergedPayload);
  const tags = Array.isArray(it?.cardTags) ? it.cardTags : (Array.isArray(it?.card?.tags) ? it.card.tags : []);

  const locationText = extractString(it?.card?.location || mergedPayload.location || mergedPayload.address);
  const cardType = it?.type || mergedPayload.type;
  const typeMeta = resolveCardTypeMeta(cardType);
  const typeIcon = getTypeIcon(cardType);

  const shouldShowDescription = Boolean(description && description.length > 0);
  const shouldShowHighlights = highlightLines.length > 0;
  const shouldShowMeta = metaPairs.length > 0;
  const shouldShowTags = tags.length > 0;
  const hasExpandableContent = shouldShowDescription || shouldShowHighlights || shouldShowMeta || shouldShowTags;
  // metaPairs already computed above
  const payloadForModal = {
    title,
    imageUrl: mergedPayload.cover_image || undefined,
    address: mergedPayload.address || it?.card?.address,
    description,
    metaPairs,
    tags,
    rawDebug: { rawMergedPayload: mergedPayload },
    card: it?.card,
    // Ensure id is always available for sharing
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
    cardId: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };

  const handlePress = () => {
    if (typeof onOpenPoi === 'function') {
      onOpenPoi(payloadForModal);
      return;
    }
    if (typeof onToggleExpand === 'function') onToggleExpand();
  };

  return (
    <TouchableOpacity style={styles.richCardContainer} onPress={hasExpandableContent ? handlePress : undefined} activeOpacity={hasExpandableContent ? 0.7 : 1}>
      <View style={styles.cardBody}>
        <View style={styles.cardHeaderRow}>
          <View style={styles.cardTitleRow}>
            <View style={[styles.cardTypeIcon, { backgroundColor: typeMeta.surface }]}>
              <WebIcon name={typeIcon} size={18} color={typeMeta.accent} />
            </View>
            <Text style={styles.cardTitle} numberOfLines={isExpanded ? undefined : 2}>{title}</Text>
          </View>
        </View>

        {locationText && (
          <View style={styles.cardLocationRow}>
            <WebIcon name="location-on" size={14} color="#9CA3AF" />
            <Text style={styles.cardLocationText} numberOfLines={1}>{locationText}</Text>
          </View>
        )}

        {isExpanded && (
          <View style={styles.cardExpandedContent}>
            {shouldShowDescription && (
              <Text style={styles.cardDescription}>{description}</Text>
            )}

            {shouldShowHighlights && (
              <View style={styles.cardHighlightBlock}>{highlightLines.map((line: any, idx: number) => <Text key={idx} style={styles.cardHighlightLine}>{line}</Text>)}</View>
            )}

            {shouldShowMeta && (
              <View style={styles.cardMetaBlock}>{metaPairs.map((meta: any, idx: number) => (
                <View key={idx} style={styles.cardMetaRow}><Text style={styles.cardMetaLabel}>{meta.label}</Text><Text style={styles.cardMetaValue}>{typeof meta.value === 'object' ? JSON.stringify(meta.value) : String(meta.value || '')}</Text></View>
              ))}</View>
            )}

            {shouldShowTags && (
              <View style={styles.cardTagList}>{tags.map((tag: any, idx: number) => (<View key={idx} style={styles.cardTagPill}><Text style={styles.cardTagText}>{tag}</Text></View>))}</View>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

export default FallbackAdapter;
